// @flow
import { dispatchEvent } from '../lib/dom';
import { getNextUniqueId } from '../lib/idGenerator';
import { resolveExecute } from '../partialUpdate/partialUpdate';
import { getNormalizedRefreshEvents, isValidForRefresh } from './inlineHOCUtils';
import { i18n, toCamelCase } from '../lib/string';
import type { PartialUpdateSettings } from '../partialUpdate/PartialUpdateSettings';
import shallowEqual from '../lib/shallowEqual';
import { isInActivePUContainer } from '../partialUpdate';

class WebComponent {
  constructor(optProps: ?Object, optInitialState: ?Object = {}) {
    this.state = {
      wasDecorated: false,
      inDocument: false,
      changed: false,
      open: false,
      progress: 0,
      normalizedRefreshEvents: null,
      processStateTokenEvents: null,
      formElement: null,
      layerElement: null,
      gridElement: null,
      processStateTokenProgress: null,
      ...optInitialState,
    };
    this.handlers = [];
    /**
     *
     * @type {Object}
     */

    this.props = {
      ...WebComponent.defaultProps,
      ...this.constructor.defaultProps,
      ...optProps,
      messages: {
        ...this.constructor.defaultProps.messages,
        ...optProps.messages,
      }
    };
    this.handleUiPush = this.handleUiPush.bind(this);
  }

  /**
   * Vrátí id, které je na dekorovaném elementu nebo vygeneruje unikátní ID, které se po celou existenci komponenty nemění
   */
  getId(): string  {
    if (!this.id) {
      this.id = getNextUniqueId();
    }
    return this.id;
  }

  /**
   * Nastaví
   * Nikdy nevolat mimo instanci třídy. Pouze uvnitř instance.
   */
  setState(state: Object): void {
    const prevState = { ...this.state };
    this.state = {
      ...this.state,
      ...state,
    };
    this.updateInternal(this.props, prevState);
  }

  /**
   * Nastaví vlastnosti komponentě
   * Nikdy nevolat v instanci třídy.
   */
  setProps(props: Object): void {
    const prevProps = { ...this.props };
    this.props = {
      ...this.props,
      ...props,
    };
    this.updateInternal(prevProps, this.state);
  }

  /**
   * Volá se na elemntu který chceme nadekorovat.
   */
  decorate(element) {
    if (this.state.inDocument) {
      throw Error('Component already rendered');
    } else if (element && this.canDecorate(element)) {
      this.setState({
        wasDecorated: true,
      });

      // Call specific component decorate logic.
      const idAttr = element.getAttribute('id');
      if (idAttr) {
        this.id = idAttr;
      }
      this.setState({
        element,
        layerElement: element.closest('.corpus-frame') || document.body,
        gridElement: element.closest('[data-fragment="grid"]'),
        formElement: element.closest('form'),
      });

      this.decorateInternal(element);

      this.elements = {};
      if (this.constructor.fragments) {
        Object.keys(this.constructor.fragments).forEach((key) => {
          const selector = this.constructor.fragments[key];
          this.elements[key] = this.element.querySelector(selector);
        });
      }

      this.connectRefreshEvents();
      this.enterDocument();
      this.didMount();

      this.setState({
        inDocument: true,
      });
      this.update({}, {}, false);
    }
  }
  connectRefreshEvents() {
    const {
      refreshEvent,
      processStateToken,
    } = this.props;

    const processStateTokenEvents = [];
    if (processStateToken) {
      if (Array.isArray(processStateToken)) {
        processStateToken.forEach((processStateTokenItem) => {
          processStateTokenEvents.push(`${processStateTokenItem.toLowerCase()}processstate`);
        });
      } else {
        processStateTokenEvents.push(`${processStateToken.toLowerCase()}processstate`);
      }
    }

    const normalizedRefreshEvents = getNormalizedRefreshEvents(refreshEvent);
    if (normalizedRefreshEvents.length > 0 || processStateTokenEvents.length > 0) {
      this.setState({
        normalizedRefreshEvents,
        processStateTokenEvents,
      });
      document.addEventListener('uiPush', this.handleUiPush);
    }
  }
  handleUiPush(e) {
    const {
      detail: {
        attr,
        eventName,
        eventNameOnlyType,
        id,
      },
    } = e;

    const {
      normalizedRefreshEvents,
      processStateTokenEvents,
    } = this.state;

    if (normalizedRefreshEvents.includes(eventNameOnlyType)) {
      this.handleRefreshEventFromProcessManager({
        type: eventNameOnlyType,
        attr,
        id,
      });
    }
    if (normalizedRefreshEvents && normalizedRefreshEvents.includes(eventName)) {
      this.handleRefreshEventFromProcessManager({
        type: eventName,
        attr,
        id,
      });
    }
    if (processStateTokenEvents && processStateTokenEvents.includes(eventName)) {
      this.handleProcessStateToken({
        type: eventName,
        attr,
        id,
      });
    }
  }
  handleRefreshEventInternal() {
    const {
      refreshEvent,
    } = this.props;

    let settings : PartialUpdateSettings = {
      target: this.id,
      alterHistory: false,
      mergeWithTargetSettings: false,
      includeToProgress: false,
      rememberPagination: true,
      onError: () => {},
      omit: ['[data-fragment="messages"]'],
      headers: {
        'RamJet-WithUiEvent': true,
      },
    };

    if (refreshEvent && refreshEvent.removeWidgetData != null) {
      settings.removeContainerWidgetData = refreshEvent.removeWidgetData;
    }
    if (refreshEvent && refreshEvent.puSettings != null) {
      settings = {
        ...settings,
        ...refreshEvent.puSettings,
      };
    }
    resolveExecute(document.getElementById(this.id), settings);
  }
  handleProcessStateToken(e) {
    const attr = e.attr;
    if (attr) {
      const message = attr.message;
      const progress = attr.progress;
      const {
        callPUAfterProcessStateTokenRefresh,
      } = this.props;
      if (message) {
        this.setState({
          processStateTokenMessage: message,
        });
      }
      if (progress) {
        this.setState({
          processStateTokenProgress: progress,
        });
      }
      if (callPUAfterProcessStateTokenRefresh && progress && progress >= 100) {
        this.handleRefreshEventInternal();
      }
    }
  }
  handleRefreshEventFromProcessManager(event) {
    const {
      value,
      refreshEvent,
    } = this.props;
    const isInActivePU = isInActivePUContainer(this.element, true);
    if (!isInActivePU && isValidForRefresh(event, refreshEvent, value)) {
      // pokud na komponentě je implementovaná metoda handleRefreshEvent, tak se zavolá ta

      if (this.wrappedComponent && this.wrappedComponent.handleRefreshEvent) {
        this.wrappedComponent.handleRefreshEvent(event);
      } else {
        this.handleRefreshEventInternal();
      }
    }
  }

  decorateInternal(element) {
    this.element = element;

    const existsEdeeDecorateMethods = window.fg && window.fg.ui && window.fg.ui.decorate;
    if (existsEdeeDecorateMethods && this.props.allowDecorateInnerElements) {
      this.decorateInnerElements();
    }
  }

  decorateInnerElements() {
    const { fg } = window;
    const { decorateSkip_ } = this.props;
    if (fg && fg.ui && fg.ui.decorate) {
      this.decoratedInnerElements = fg.ui.decorate.decorateInnerElements(this.element, undefined, undefined, decorateSkip_);
    }
  }

  /**
   *
   * @param eventType
   * @param selector
   * @param handler
   */
  on(eventType: string, selector: (string | (e: Event) => void), handler: ?(e: Event) => void) {
    if (typeof selector === 'string') {
      const eventHandler = (e) => {
        const closest = e.target.closest(selector);
        if (closest) {
          // change event currentTarget to closest
          // IE11 z currentTarget nepřepíše
          Object.defineProperty(e, 'currentTarget', {
            value: closest,
            writable: false,
            configurable: true,
          });
          // IE11 z currentTarget nepřepíše
          // proto se zapíše nová vlastnost closestTarget
          Object.defineProperty(e, 'closestTarget', {
            value: closest,
            writable: false,
            configurable: true,
          });
          handler(e);
        }
      };

      this.element.addEventListener(eventType, eventHandler, eventType === 'blur');
      this.handlers.push({
        eventType,
        eventHandler,
        selector,
      });
    } else {
      const eventHandler = selector;
      let blurEventHandler;
      if (eventType === 'blur') {
        blurEventHandler = (e) => {
          setTimeout(() => {
            if (this.element && !this.element.contains(document.activeElement)) {
              eventHandler(e);
            }
          }, 1);
        };
      }
      this.element.addEventListener(eventType, blurEventHandler || eventHandler, eventType === 'blur' || eventType === 'focus');
      this.handlers.push({
        eventType,
        eventHandler: blurEventHandler || eventHandler,
      });
    }
  }

  /**
   * dispatchne/vystřelí na elment komponenty požadovaný event.
   * type properta definuje typ eventu
   * ostatní property eventObjectu jsou předány do detailu [CustomEventu]{@link https://developer.mozilla.org/cs/docs/Web/API/CustomEvent}
   */
  dispatchEvent(eventObject: Object) {
    /*window.$(this.element).trigger({
      ...eventObject,
      targetComponent: this,
    });*/
    dispatchEvent(this.element, eventObject.type, {
      targetComponent: this,
      ...eventObject,
    });
  }

  /**
   * Dispatchne change event
   */
  dispatchChangeEvent(value) {
    if (this.props.dispatchChangeEvent) {
      this.dispatchEvent({ type: 'change', value });
      this.setState({
        changed: true,
      });
    }
  };

  /**
   *
   * @private
   */
  updateInternal(prevProps, prevState) {
    if (
      this.state.inDocument &&
      (
        !shallowEqual(this.state, prevState) ||
        !shallowEqual(this.props, prevProps)
      )
    ) {
      clearTimeout(this.updateTimeOut);
      this.updateTimeOut = setTimeout(() => {
        this.updateInfoMessages(prevProps, prevState, true);
        this.update(prevProps, prevState, true);
      }, 2);
    }
  }

  /**
   * Metoda která slouží pro zobrazování např. validačních chyb a informací pro vyplnění komponenty
   * @param prevProps
   * @param prevState
   */
  updateInfoMessages(prevProps: Object, prevState: Object) {

  }
  /**
   * V teto metodě by se měla vykonávat všechna logika nahrazování DOMu
   * Volá se vždy po aktualizaci stavu nebo props pomocí
   * metod setState a setProps a po nadekorování elementu
   */
  update(prevProps: Object, prevState: Object, inDocument: boolean) {

  }

  canDecorate() {
    return true;
  }

  /**
   * Zavolá se po úspěšném mountnutí na dekorovaný element
   * Od této fáze je dostupný this.element a properties z data attributů
   */
  didMount() {

  }

  /**
   * @deprecated
   * didMount alias
   */
  enterDocument() {

  }

  /**
   * Volá se před tím než je komponenta smazána
   * V této metodě by se měly smazat všechny vnitřní proměnné
   * a event listenery, které nebyly přidány pomocí metody @see {#on}
   */
  willUnMount() {

  }

  exitDocument() {
    this.willUnMount();
    this.setState({
      inDocument: false,
    });
  }

  dispose() {
    this.exitDocument();
    if (this.decoratedInnerElements && this.decoratedInnerElements.length > 0) {
      this.decoratedInnerElements.forEach((component, index) => {
        if (component && component.dispose) {
          component.dispose();
        }
      });
      this.decoratedInnerElements = null;
    }
    this.handlers.forEach(({ eventHandler, eventType }) => {
      this.element.removeEventListener(eventType, eventHandler);
    });
    document.removeEventListener('uiPush', this.handleUiPush);
    this.id = null;
    this.element = null;
  }

  /**
   * Vrátí form data parentího formuláře
   */
  DOMGetParentFormData(remove: Array<string>): Object {
    const { formElement } = this.state;
    if (formElement) {
      const formData = {};
      $(formElement).serializeArray().forEach(({ value, name }) => {
        if (!remove || (remove && !remove.includes(name))) {
          if (!formData[name]) {
            formData[name] = [];
          }
          formData[name].push(value);
        }
      });
      return formData;
    }
    return {};
  }
  i18n(message, optValues) {
    const { messages = {}} = this.props;
    return i18n(message, optValues, this.constructor.name, messages);
  }
}

/**
 * Výchozí vlastnosti WebComponenty
 * Vždy jsou mergovány do vlastností zděděných komponent
 */
WebComponent.defaultProps = {
  messages: null,
  ignoreChanges: false,
  refreshEvent: null,
  lang: 'cs',
  /**
   * [cs] registrace tokenu podle ktereho se komponenta sama aktualizuje. Vyuziva se pro zobrazeni
   * progesu RamJet commandu.
   * @type {string?}
   */
  processStateToken: null,
  allowDecorateInnerElements: true,
  dispatchChangeEvent: true,
  callPUAfterProcessStateTokenRefresh: true,
  tooltip: null,
  error: null,
};

export default WebComponent;



// WEBPACK FOOTER //
// ./node_modules/fg-frontend/src/webComponents/WebComponent.js